/** @jsxImportSource @emotion/react */
import * as React from "react";
import { addHtmlListener } from "@base/util/BrowserUtil";
import { CssUtil } from "@base/util/CssUtil";
import { Disposers } from "@base/util/Disposers";
import { AppColors } from "@biz/branding/AppColors";

const colors: string[] = ["#FFDD00", "#48759A", AppColors.main.dashboard, AppColors.main.projects, AppColors.main.offer, AppColors.green, AppColors.lightGrey, "white"];

const animate = (circle?: Element) => {
  if (circle) {
    circle.setAttribute("strokeWidth", CssUtil.px(0.5 + Math.random() * 4));
    const colorIndex = Math.floor(Math.random() * colors.length - 0.001);
    // console.log("ColorIndex", colorIndex);
    circle.setAttribute("stroke", colors[colorIndex]);
  }
};

function SvgSpinnerInner(props: React.SVGProps<SVGSVGElement>) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  React.useEffect(() => {

    const disposers = new Disposers();

    const circle1 = svgRef.current?.getElementById("circle1");
    const anim1 = svgRef.current?.getElementById("circleAnim1");
    if (anim1)
      disposers.push(addHtmlListener(anim1, "repeatEvent", () => animate(circle1)));

    const circle2 = svgRef.current?.getElementById("circle2");
    const anim2 = svgRef.current?.getElementById("circleAnim2");
    if (anim2)
      disposers.push(addHtmlListener(anim2, "repeatEvent", () => animate(circle2)));

    return () => {
      disposers.dispose();
    };
  }, []);
  return (
    <svg
      width={250}
      height={250}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid"
      className="prefix__lds-ripple"
      ref={svgRef}
      style={{
        background: "0 0",
      }}
      {...props}
    >
      <circle id="circle1" cx={50} cy={50} r={4.719} fill="none" stroke={colors[0]} strokeWidth={2}>
        <animate id="circleAnim1" attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur={3} keySplines="0 0.2 0.8 1" begin="-1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur={3} keySplines="0.2 0 0.8 1" begin="-1.5s" repeatCount="indefinite" />
      </circle>
      <circle id="circle2" cx={50} cy={50} r={27.591} fill="none" stroke={colors[1]} strokeWidth={2}>
        <animate id="circleAnim2" attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur={3} keySplines="0 0.2 0.8 1" begin="0s" repeatCount="indefinite" />
        <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur={3} keySplines="0.2 0 0.8 1" begin="0s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

export const SvgSpinner = React.memo((props: { visible: boolean; title?: string | React.ReactNode; backgroundColor?: string; zIndex?: number }) => {
  const { visible, title, backgroundColor, zIndex = 1000 } = props;

  const [showSpinner, setShowSpinner] = React.useState(true);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;
    if (visible)
      setShowSpinner(true);
    return () => { mounted.current = false; };
  }, [visible])

  if (!showSpinner && !visible)
    return null;

  return (
    <div css={{
      position: "absolute",
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex,
      opacity: visible ? 1.0 : 0.0,
      transition: "opacity 0.3s linear",
      pointerEvents: visible ? "all" : "none",
      backgroundColor: backgroundColor || CssUtil.colorWithOpacity("#000", 0.7)
    }} onTransitionEnd={() => {
      if (!props.visible && mounted.current) setShowSpinner(false)
    }}>
      {showSpinner && <>
        <SvgSpinnerInner />
        {
          title && <div css={{
            position: "absolute",
            color: "white",
            textShadow: "0px 0px 6px rgba(0, 0, 0, 1.0)",
          }}>{title}</div>
        }
      </>}
    </div>
  );
});

SvgSpinner.displayName = "SvgSpinner";
